import type { WeatherDailyBucket } from "@/hooks/use-dashboard-weather";
import type { WasteStatsBucket } from "@/hooks/use-waste-stats";
import { shiftDay, weekStartDay, zonedDay, zonedDayEnd, zonedDayStart } from "@/lib/zoned-day";

export type Granularity = "day" | "week" | "month";

/** Days shown on the Daily granularity (the design's "last 2 weeks"). */
export const DAILY_WINDOW_DAYS = 14;

/**
 * The fixed window each granularity plots — independent of the dashboard's period
 * control (POS2-870): Daily = last 2 weeks, Weekly = last 3 months, Monthly = last
 * 12 months. `to` closes at the end of today so today's partial bucket is included
 * but future-dated events are not: an open `to` reaches the stats RPC as no `date_to`
 * and is read there as `COALESCE(p_date_to, 'infinity')` (POS2-835). `from` anchors
 * the window. Weekly/Monthly series are aggregated client-side from the returned
 * daily buckets (existing `aggregateWeekly`/`aggregateMonthly`).
 *
 * Both bounds sit at midnight in `timeZone` — the site's zone, the one the RPC buckets
 * in (POS2-1039). Built as civil days first so the spans are whole calendar weeks and
 * months whatever DST does inside them.
 */
export function trendWindow(
  granularity: Granularity,
  now: Date,
  timeZone: string,
): { from: string; to: string } {
  const today = zonedDay(now, timeZone);
  const to = zonedDayEnd(today, timeZone).toISOString();
  const from = (day: string) => zonedDayStart(day, timeZone).toISOString();
  if (granularity === "week") return { from: from(shiftDay(weekStartDay(today), -12 * 7)), to };
  if (granularity === "month") {
    const [year, month] = today.split("-").map(Number);
    return { from: from(monthStartDay(year, month - 11)), to };
  }
  return { from: from(shiftDay(today, -(DAILY_WINDOW_DAYS - 1))), to };
}

/** First civil day of `month` (1-based, may fall outside 1–12 — it rolls the year). */
function monthStartDay(year: number, month: number): string {
  return new Date(Date.UTC(year, month - 1, 1)).toISOString().slice(0, 10);
}

/**
 * The previous-equivalent period for the Daily ghost line: the 14 days immediately
 * before the Daily window, closed at the last millisecond of the day before it starts.
 * Its buckets are plotted at the same x-positions as the current window (aligned by
 * index in the chart).
 */
export function trendGhostWindow(now: Date, timeZone: string): { from: string; to: string } {
  const currentStart = shiftDay(zonedDay(now, timeZone), -(DAILY_WINDOW_DAYS - 1));
  return {
    from: zonedDayStart(shiftDay(currentStart, -DAILY_WINDOW_DAYS), timeZone).toISOString(),
    to: zonedDayEnd(shiftDay(currentStart, -1), timeZone).toISOString(),
  };
}

/**
 * The horizontal target value: `waste_pct_target` % of food spend at the granularity's
 * unit. The design draws it off a weekly spend (per-week = spend×%, per-day = ÷7,
 * per-month = ×4.33), so callers pass spend already normalised to a week. Null when
 * spend or target is missing — the caller then omits the line and its legend item.
 * This is a cost (£) value, so it only overlays the cost lens.
 */
export const WEEKS_PER_MONTH = 4.33;

export function targetForUnit(
  weeklySpend: number | null,
  targetPct: number | null,
  granularity: Granularity,
): number | null {
  if (weeklySpend == null || targetPct == null) return null;
  const perWeek = (weeklySpend * targetPct) / 100;
  if (granularity === "day") return perWeek / 7;
  if (granularity === "month") return perWeek * WEEKS_PER_MONTH;
  return perWeek;
}

/**
 * Normalise the dashboard period's food spend to a weekly rate for the target line.
 * `periodDays` is the span of the dashboard's date range; a week of spend over a
 * shorter/longer period scales linearly. Null when spend is missing.
 */
export function weeklySpendFromPeriod(foodSpend: number | null, periodDays: number): number | null {
  if (foodSpend == null || periodDays <= 0) return null;
  return (foodSpend * 7) / periodDays;
}

/**
 * A one-glyph sky summary for the Daily weather row, derived from the cached metrics
 * (Open-Meteo gives no weather code): rain when it precipitated, otherwise sun/cloud
 * by cloud cover, falling back to sunshine hours. Null when the day has no reading.
 */
export function weatherEmoji(bucket: WeatherDailyBucket): string | null {
  if (bucket.precipitation_mm != null && bucket.precipitation_mm >= 1) return "🌧️";
  if (bucket.cloud_cover_pct != null) return bucket.cloud_cover_pct < 40 ? "☀️" : "⛅";
  if (bucket.sunshine_hours != null) return bucket.sunshine_hours >= 5 ? "☀️" : "⛅";
  return null;
}

/**
 * A continuous run of `count` civil days starting at `startDay` (`YYYY-MM-DD`), filling
 * zero buckets for days with no events — the sparse stats `by_day` omits empty days, but
 * the Daily chart needs a fixed 14-column axis so weekend bands, the weather row, and the
 * ghost line all align by index. Keys are the site-local days the RPC buckets by.
 */
export function densifyDays(
  buckets: WasteStatsBucket[],
  startDay: string,
  count: number,
): WasteStatsBucket[] {
  const byKey = new Map(buckets.map((b) => [b.key, b]));
  const out: WasteStatsBucket[] = [];
  for (let i = 0; i < count; i++) {
    const key = shiftDay(startDay, i);
    out.push(byKey.get(key) ?? { key, event_count: 0, total_weight: 0, total_cost: 0 });
  }
  return out;
}

/**
 * Weekend shading spans for a continuous run of day keys (`YYYY-MM-DD`): one `{ x1, x2 }`
 * per maximal Sat–Sun run, where `x2` is the key of the day after the run (so a
 * ReferenceArea from x1→x2 shades the whole weekend block), clamped to the last key at the
 * edge. Takes bare keys so the dashboard trend (POS2-870) and Explore (POS2-883) share it
 * over their different bucket shapes — and so the weekday is read off the calendar date
 * itself, never off a `new Date(key)` in the viewer's zone.
 */
export function weekendSpans(dayKeys: string[]): { x1: string; x2: string }[] {
  const isWeekend = (key: string) => {
    const dow = new Date(`${key}T00:00:00Z`).getUTCDay();
    return dow === 0 || dow === 6;
  };
  const spans: { x1: string; x2: string }[] = [];
  let i = 0;
  while (i < dayKeys.length) {
    if (!isWeekend(dayKeys[i])) {
      i++;
      continue;
    }
    const start = i;
    while (i < dayKeys.length && isWeekend(dayKeys[i])) i++;
    spans.push({ x1: dayKeys[start], x2: dayKeys[i] ?? dayKeys[dayKeys.length - 1] });
  }
  return spans;
}
